(function (Drupal, once) {
  'use strict';

  const choiceHandlers = new WeakMap();
  const controlHandlers = new WeakMap();

  Drupal.behaviors.catalogVariationChoice = {
    attach(context) {
      once('catalog-variation-choice', '.view-catalog-listview .product-row-group', context)
        .forEach((group) => {
          const radios = Array.from(group.querySelectorAll('[data-catalog-variation]'));
          const actions = Array.from(group.querySelectorAll('[data-catalog-action]'));
          if (!radios.length || !actions.length) return;
          const actionCell = group.querySelector('.catalog-purchase-action');
          let transition;

          const updateAction = () => {
            const selected = radios.find((radio) => radio.checked);
            actions.forEach((action) => {
              action.hidden = action.dataset.catalogAction !== selected?.value;
            });
          };
          const transitionAction = async () => {
            transition?.abort();
            const current = new AbortController();
            transition = current;
            actionCell.inert = true;
            try {
              await Drupal.adocsProductFade([actionCell], false, current.signal);
              if (current.signal.aborted || !group.isConnected) return;
              updateAction();
              await Drupal.adocsProductFade([actionCell], true, current.signal);
            } finally {
              if (transition === current) actionCell.inert = false;
            }
          };
          const onChange = (event) => {
            if (event.target.matches('[data-catalog-variation]')) transitionAction();
          };
          group.addEventListener('change', onChange);
          choiceHandlers.set(group, { onChange, cancel: () => transition?.abort() });
          updateAction();
          radios.forEach((radio) => { radio.disabled = false; });
        });
    },
    detach(context, settings, trigger) {
      if (trigger !== 'unload') return;
      once.remove('catalog-variation-choice', '.view-catalog-listview .product-row-group', context)
        .forEach((group) => {
          const handlers = choiceHandlers.get(group);
          group.removeEventListener('change', handlers?.onChange);
          handlers?.cancel();
          choiceHandlers.delete(group);
        });
    },
  };

  /**
   * Shares the existing toggle between the desktop header and mobile controls.
   */
  Drupal.behaviors.catalogListviewToggle = {
    attach(context) {
      once(
        'catalog-listview-toggle',
        '.view-catalog-listview',
        context
      ).forEach((view) => {
        const toggleBlock = document.querySelector(
          '#block-adocs-viewtogglegridlist'
        );

        const home = toggleBlock?.parentElement;
        const desktopTarget = view.querySelector('thead th:last-child');
        const mobileTarget = view.querySelector('.catalog-list-toggle');
        const select = view.querySelector('.catalog-list-sort-select');
        const scroll = view.querySelector('.catalog-list-scroll');
        const navigation = document.querySelector('.sticky-navigation');
        const headerLinks = view.querySelectorAll('thead a');
        const purchaseCells = Array.from(view.querySelectorAll('.product-row-group')).map((group) => ({
          first: group.firstElementChild,
          last: group.lastElementChild,
          cell: group.querySelector('.views-field-adocs-add-to-cart-link'),
          span: group.children.length,
        }));
        // Keep aligned with --phone in variables.css.
        const media = window.matchMedia('(width < 768px)');
        let stickyFrame = null;
        const updateStickyOffset = () => {
          stickyFrame = null;
          // Overlap the fractional header edge instead of leaving a visible seam.
          const offset = navigation
            ? Math.max(0, Math.floor(navigation.getBoundingClientRect().bottom))
            : 0;
          view.style.setProperty('--catalog-sticky-offset', `${offset}px`);
        };
        const scheduleStickyOffset = () => {
          if (stickyFrame === null) stickyFrame = requestAnimationFrame(updateStickyOffset);
        };
        const navigationResize = navigation && window.ResizeObserver
          ? new ResizeObserver(scheduleStickyOffset)
          : null;
        const placeControls = () => {
          const target = media.matches ? mobileTarget : desktopTarget;
          if (toggleBlock && target) target.append(toggleBlock);
          headerLinks.forEach((link) => {
            if (media.matches) link.setAttribute('tabindex', '-1');
            else link.removeAttribute('tabindex');
          });
          // The table fits intrinsically at every active table breakpoint;
          // avoid a non-scrolling focus stop that would also trap sticky cells.
          scroll?.removeAttribute('tabindex');
          // CSS reorders the layout; move this single cell so Tab also reaches
          // the shared action after the last selected variation on mobile.
          purchaseCells.forEach(({ first, last, cell, span }) => {
            if (!cell) return;
            const row = media.matches ? last : first;
            if (cell.parentElement !== row) row.append(cell);
            cell.setAttribute('rowspan', media.matches ? '1' : String(span));
          });
        };
        const onSort = () => {
          if (select.value) window.location.assign(select.value);
        };
        const onSortPointerDown = () => {
          select.classList.add('is-pointer-focus');
        };
        const onSortKeyDown = () => {
          select.classList.remove('is-pointer-focus');
        };
        const onSortBlur = () => {
          select.classList.remove('is-pointer-focus');
        };
        select?.addEventListener('change', onSort);
        if (select) {
          select.addEventListener('pointerdown', onSortPointerDown);
          select.addEventListener('keydown', onSortKeyDown);
          select.addEventListener('blur', onSortBlur);
          select.closest('.catalog-list-sort').hidden = false;
        }
        media.addEventListener('change', placeControls);
        window.addEventListener('scroll', scheduleStickyOffset, { passive: true });
        window.addEventListener('resize', scheduleStickyOffset);
        if (navigationResize) navigationResize.observe(navigation);
        placeControls();
        scheduleStickyOffset();
        controlHandlers.set(view, {
          media,
          placeControls,
          select,
          onSort,
          onSortPointerDown,
          onSortKeyDown,
          onSortBlur,
          toggleBlock,
          home,
          navigationResize,
          scheduleStickyOffset,
          cancelStickyOffset: () => {
            if (stickyFrame !== null) cancelAnimationFrame(stickyFrame);
          },
        });
      });
    },
    detach(context, settings, trigger) {
      if (trigger !== 'unload') return;
      once.remove('catalog-listview-toggle', '.view-catalog-listview', context)
        .forEach((view) => {
          const handlers = controlHandlers.get(view);
          if (!handlers) return;
          handlers.media.removeEventListener('change', handlers.placeControls);
          handlers.select?.removeEventListener('change', handlers.onSort);
          handlers.select?.removeEventListener('pointerdown', handlers.onSortPointerDown);
          handlers.select?.removeEventListener('keydown', handlers.onSortKeyDown);
          handlers.select?.removeEventListener('blur', handlers.onSortBlur);
          window.removeEventListener('scroll', handlers.scheduleStickyOffset);
          window.removeEventListener('resize', handlers.scheduleStickyOffset);
          handlers.navigationResize?.disconnect();
          handlers.cancelStickyOffset();
          view.style.removeProperty('--catalog-sticky-offset');
          // Retain the block when Views replaces its rendered content.
          if (handlers.home?.isConnected && handlers.toggleBlock) {
            handlers.home.append(handlers.toggleBlock);
          }
          controlHandlers.delete(view);
        });
    }
  };

})(Drupal, once);
